<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4 pb-3 border-bottom border-subtle d-flex flex-column flex-md-row justify-content-between align-items-md-center">
      <div>
        <h3 class="text-white font-weight-bold mb-1">
          <i class="mdi mdi-checkbox-marked-circle-outline text-warning mr-2"></i>{{ $t('approvals.title') }}
        </h3>
        <p class="text-muted small mb-0">{{ $t('approvals.subtitle') }}</p>
      </div>
      <div class="mt-3 mt-md-0 d-flex align-items-center">
        <button class="btn btn-outline-secondary btn-sm px-3" @click="fetchData" :disabled="loading">
          <i class="mdi mdi-refresh mr-1" :class="{ 'mdi-spin': loading }"></i>
          {{ $t('common.refresh') }}
        </button>
      </div>
    </div>

    <!-- Metric Stat Cards -->
    <div class="col-12 mb-4">
      <div class="row">
        <div class="col-sm-6 col-lg-3 mb-3 mb-lg-0">
          <div class="card border border-secondary bg-dark shadow-sm">
            <div class="card-body p-3 d-flex align-items-center justify-content-between">
              <div>
                <span class="text-muted small text-uppercase font-weight-bold d-block">{{ $t('approvals.pendingReview') }}</span>
                <h3 class="text-warning font-weight-bold mb-0 mt-1">{{ countByStatus('pending') }}</h3>
              </div>
              <div class="rounded p-2 text-warning" style="background: rgba(245, 158, 11, 0.15);">
                <i class="mdi mdi-clock-alert-outline" style="font-size: 1.5rem;"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-lg-3 mb-3 mb-lg-0">
          <div class="card border border-secondary bg-dark shadow-sm">
            <div class="card-body p-3 d-flex align-items-center justify-content-between">
              <div>
                <span class="text-muted small text-uppercase font-weight-bold d-block">{{ $t('approvals.approved') }}</span>
                <h3 class="text-success font-weight-bold mb-0 mt-1">{{ countByStatus('approved') }}</h3>
              </div>
              <div class="rounded p-2 text-success" style="background: rgba(16, 185, 129, 0.15);">
                <i class="mdi mdi-check-decagram-outline" style="font-size: 1.5rem;"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-lg-3 mb-3 mb-lg-0">
          <div class="card border border-secondary bg-dark shadow-sm">
            <div class="card-body p-3 d-flex align-items-center justify-content-between">
              <div>
                <span class="text-muted small text-uppercase font-weight-bold d-block">{{ $t('approvals.rejected') }}</span>
                <h3 class="text-danger font-weight-bold mb-0 mt-1">{{ countByStatus('rejected') }}</h3>
              </div>
              <div class="rounded p-2 text-danger" style="background: rgba(239, 68, 68, 0.15);">
                <i class="mdi mdi-close-octagon-outline" style="font-size: 1.5rem;"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-lg-3">
          <div class="card border border-secondary bg-dark shadow-sm">
            <div class="card-body p-3 d-flex align-items-center justify-content-between">
              <div>
                <span class="text-muted small text-uppercase font-weight-bold d-block">{{ $t('approvals.totalGoldDistributed') }}</span>
                <h3 class="text-white font-weight-bold mb-0 mt-1">{{ totalGoldDistributed.toLocaleString() }} <small class="text-warning">Gold</small></h3>
              </div>
              <div class="rounded p-2 text-warning" style="background: rgba(245, 158, 11, 0.15);">
                <i class="mdi mdi-circle-multiple" style="font-size: 1.5rem;"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 grid-margin">
      <div class="card shadow-sm">
        <div class="card-body p-4">
          <!-- Filters & Search Toolbar -->
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-2">
            <!-- Status Filter Tabs & Batch Action -->
            <div class="d-flex flex-wrap align-items-center gap-2 mb-2 mb-md-0">
              <div class="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  class="btn font-weight-bold"
                  :class="statusFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="statusFilter = 'all'"
                >
                  {{ $t('common.all') }} ({{ rewardApproval.length }})
                </button>
                <button
                  type="button"
                  class="btn font-weight-bold"
                  :class="statusFilter === 'pending' ? 'btn-warning text-dark' : 'btn-outline-secondary'"
                  @click="statusFilter = 'pending'"
                >
                  {{ $t('common.pending') }} ({{ countByStatus('pending') }})
                </button>
                <button
                  type="button"
                  class="btn font-weight-bold"
                  :class="statusFilter === 'approved' ? 'btn-success' : 'btn-outline-secondary'"
                  @click="statusFilter = 'approved'"
                >
                  {{ $t('common.approved') }} ({{ countByStatus('approved') }})
                </button>
                <button
                  type="button"
                  class="btn font-weight-bold"
                  :class="statusFilter === 'rejected' ? 'btn-danger' : 'btn-outline-secondary'"
                  @click="statusFilter = 'rejected'"
                >
                  {{ $t('common.rejected') }} ({{ countByStatus('rejected') }})
                </button>
              </div>

              <button
                v-if="countByStatus('pending') > 0"
                type="button"
                class="btn btn-sm btn-outline-success font-weight-bold ml-md-2"
                @click="batchApprove"
                :disabled="loading"
              >
                <i class="mdi mdi-check-all mr-1"></i> {{ $currentLocale === 'id' ? 'Setujui Semua Sekaligus' : 'Quick Approve All' }} ({{ countByStatus('pending') }})
              </button>
            </div>

            <!-- Search Input -->
            <div class="search-box" style="max-width: 320px; width: 100%;">
              <div class="input-group input-group-sm">
                <span class="input-group-text bg-input border-card text-muted">
                  <i class="mdi mdi-magnify"></i>
                </span>
                <input
                  v-model.trim="search"
                  type="text"
                  class="form-control"
                  :placeholder="$t('approvals.searchPlaceholder')"
                />
                <button
                  v-if="search"
                  class="btn btn-outline-secondary"
                  type="button"
                  @click="search = ''"
                >
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <table class="table table-hover align-middle" style="width: 100%;">
              <thead>
                <tr class="text-muted small text-uppercase">
                  <th style="width: 38px;">No</th>
                  <th style="width: 175px;">{{ $t('approvals.employee') }}</th>
                  <th style="width: 205px;">{{ $t('approvals.questRule') }}</th>
                  <th style="width: 125px;">{{ $currentLocale === 'id' ? 'Bukti / Lampiran' : 'Proof / Attachment' }}</th>
                  <th style="width: 105px;">{{ $t('approvals.submittedAt') }}</th>
                  <th style="width: 105px;">{{ $t('approvals.verificationStatus') }}</th>
                  <th class="text-right" style="width: 115px; padding-right: 0.75rem;">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(data, index) in paginatedApproval" :key="data.id">
                  <td class="text-muted">{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <div
                        class="rounded-circle mr-2 d-flex align-items-center justify-content-center text-white font-weight-bold flex-shrink-0"
                        style="width: 32px; height: 32px; background: rgba(99, 102, 241, 0.2); border: 1px solid rgba(99, 102, 241, 0.4);"
                      >
                        {{ (data.user?.name || 'P')[0].toUpperCase() }}
                      </div>
                      <div class="overflow-hidden">
                        <div class="text-white font-weight-bold text-truncate" :title="data.user?.name">{{ data.user?.name || ($currentLocale === 'id' ? 'Karyawan' : 'Employee') }}</div>
                        <small class="text-muted text-truncate d-block" :title="data.user?.email">{{ data.user?.email || '-' }}</small>
                      </div>
                    </div>
                  </td>
                  <td class="overflow-hidden">
                    <div class="text-white font-weight-medium text-truncate" :title="data.rule?.name">
                      <i class="mdi mdi-flag-checkered text-warning mr-1"></i>
                      {{ data.rule?.name || ($currentLocale === 'id' ? 'Tugas Misi' : 'Quest Task') }}
                    </div>
                    <small class="text-muted text-truncate d-block" v-if="data.rule?.gold || data.rule?.non_gold">
                      {{ $t('quests.reward') }}: {{ data.rule?.gold ? data.rule.gold + ' Gold' : data.rule.non_gold }}
                    </small>
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-1 flex-wrap">
                      <button
                        v-if="data.evidence_path || data.evidence"
                        type="button"
                        @click="viewProof(data)"
                        class="btn btn-xs btn-outline-info mr-1"
                        :title="$currentLocale === 'id' ? 'Lihat Foto Bukti' : 'View Photo Proof'"
                      >
                        <i class="mdi mdi-image-area mr-1"></i> {{ $currentLocale === 'id' ? 'Foto' : 'Photo' }}
                      </button>
                      <a
                        v-if="data.external_link"
                        :href="data.external_link"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="btn btn-xs btn-outline-primary mr-1"
                        :title="$currentLocale === 'id' ? 'Buka Tautan Eksternal' : 'Open External Link'"
                      >
                        <i class="mdi mdi-link-variant mr-1"></i> {{ $currentLocale === 'id' ? 'Tautan' : 'Link' }}
                      </a>
                      <span
                        v-if="data.comment"
                        class="badge badge-dark border border-secondary text-muted"
                        :title="data.comment"
                      >
                        <i class="mdi mdi-comment-text-outline"></i>
                      </span>
                      <span v-if="!data.evidence_path && !data.evidence && !data.external_link && !data.comment" class="text-muted small">
                        -
                      </span>
                    </div>
                  </td>
                  <td class="text-muted small">
                    {{ formatDate(data.created_at) }}
                  </td>
                  <td>
                    <span
                      class="badge font-weight-bold"
                      :class="{
                        'badge-warning': data.status === 'pending',
                        'badge-success': data.status === 'approved',
                        'badge-danger': data.status === 'rejected'
                      }"
                    >
                      <i
                        class="mdi mr-1"
                        :class="{
                          'mdi-clock-outline': data.status === 'pending',
                          'mdi-check-circle': data.status === 'approved',
                          'mdi-close-circle': data.status === 'rejected'
                        }"
                      ></i>
                      {{ data.status === 'approved' ? $t('common.approved').toUpperCase() : (data.status === 'rejected' ? $t('common.rejected').toUpperCase() : $t('common.pending').toUpperCase()) }}
                    </span>
                  </td>
                  <td class="text-right">
                    <div class="d-inline-flex align-items-center gap-1">
                      <template v-if="data.status === 'pending'">
                        <button
                          type="button"
                          class="btn btn-xs btn-success font-weight-bold mr-1"
                          @click="quickApprove(data)"
                          :disabled="actionLoading === data.id"
                          :title="$currentLocale === 'id' ? 'Setujui Langsung' : 'Quick Approve'"
                        >
                          <i class="mdi mdi-check"></i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-xs btn-danger font-weight-bold mr-1"
                          @click="quickReject(data)"
                          :disabled="actionLoading === data.id"
                          :title="$currentLocale === 'id' ? 'Tolak dengan Alasan' : 'Reject with Note'"
                        >
                          <i class="mdi mdi-close"></i>
                        </button>
                      </template>
                      <router-link
                        :to="{ name: 'EditRewardApproval', params: { id: String(data.id) } }"
                        class="btn btn-xs btn-outline-secondary"
                        :title="$t('approvals.reviewSubmission')"
                      >
                        <i class="mdi mdi-eye"></i>
                      </router-link>
                    </div>
                  </td>
                </tr>

                <tr v-if="paginatedApproval.length === 0">
                  <td colspan="7" class="text-center py-5">
                    <EmptyState
                      icon="mdi-check-decagram-outline"
                      :title="$t('approvals.emptyTitle')"
                      :description="$t('approvals.emptyDesc')"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="filteredApproval.length > itemsPerPage" class="mt-3">
            <PaginationComponents
              :totalItems="filteredApproval.length"
              :itemsPerPage="itemsPerPage"
              :currentPage="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getRewardApproval, updateRewardApproval } from "../../services/approvalService";
import PaginationComponents from "../../components/PaginationComponents.vue";
import EmptyState from "../../components/EmptyState.vue";
import config from "../../config/config";
import Swal from "sweetalert2";
import moment from "moment";

export default {
  name: "RewardApproval",
  components: {
    PaginationComponents,
    EmptyState,
  },
  data() {
    return {
      search: "",
      statusFilter: "all",
      rewardApproval: [],
      currentPage: 1,
      itemsPerPage: 10,
      loading: false,
      actionLoading: null,
    };
  },
  computed: {
    totalGoldDistributed() {
      return this.rewardApproval
        .filter((i) => (i.status || "").toLowerCase() === "approved")
        .reduce((sum, item) => sum + (Number(item.gold) || Number(item.rule?.gold) || 0), 0);
    },
    filteredApproval() {
      let list = this.rewardApproval;
      if (this.statusFilter !== "all") {
        list = list.filter((item) => (item.status || "").toLowerCase() === this.statusFilter);
      }
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter((item) => {
          const uMatch = (item.user?.name || "").toLowerCase().includes(q);
          const rMatch = (item.rule?.name || "").toLowerCase().includes(q);
          const eMatch = (item.user?.email || "").toLowerCase().includes(q);
          return uMatch || rMatch || eMatch;
        });
      }
      return list;
    },
    paginatedApproval() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredApproval.slice(start, end);
    },
  },
  watch: {
    search() {
      this.currentPage = 1;
    },
    statusFilter() {
      this.currentPage = 1;
    },
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const response = await getRewardApproval();
        this.rewardApproval = response.data || [];
      } catch (error) {
        console.error("Error fetching rewardApprovals:", error);
      } finally {
        this.loading = false;
      }
    },
    countByStatus(status) {
      return this.rewardApproval.filter((item) => (item.status || "").toLowerCase() === status).length;
    },
    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD MMM YYYY, HH:mm");
    },
    onPageChanged(page) {
      this.currentPage = page;
    },
    getEvidenceUrl(item) {
      const ev = item.evidence_path || item.evidence;
      if (!ev) return null;
      if (ev.startsWith("http")) return ev;
      return `${config.STORAGE_URL}${ev}`;
    },
    viewProof(item) {
      const url = this.getEvidenceUrl(item);
      if (!url) return;
      const isId = this.$currentLocale === "id";
      Swal.fire({
        title: item.rule?.name || (isId ? "Pratinjau Bukti Klaim" : "Proof Preview"),
        html: `
          <div class="text-left mb-2 small text-muted">${isId ? "Dikirim oleh" : "Submitted by"}: <strong class="text-white">${item.user?.name || "Player"}</strong></div>
          ${item.comment ? `<div class="p-2 rounded text-left small mb-3" style="background: #1e293b; color: #cbd5e1; border: 1px solid #334155;"><em>"${item.comment}"</em></div>` : ""}
          <div style="max-height: 480px; overflow: auto; text-align: center;">
            <img src="${url}" class="img-fluid rounded border border-secondary" style="max-height: 450px;" alt="Proof"/>
          </div>
          <div class="mt-3 text-center">
            <a href="${url}" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-outline-info">
              <i class="mdi mdi-open-in-new mr-1"></i> ${isId ? "Buka Ukuran Penuh" : "Open Full Size"}
            </a>
          </div>
        `,
        showCloseButton: true,
        showConfirmButton: false,
        background: "#141724",
        customClass: {
          popup: "border border-secondary",
        },
      });
    },
    async quickApprove(item) {
      const isId = this.$currentLocale === "id";
      const questName = item.rule?.name || (isId ? "quest ini" : "this quest");
      const playerName = item.user?.name || "player";
      const rewardGold = item.gold || item.rule?.gold || 0;

      const result = await Swal.fire({
        title: isId ? "Setujui Bukti Quest?" : "Approve Quest Submission?",
        html: isId
          ? `Konfirmasi persetujuan untuk <b>${playerName}</b> pada <b>${questName}</b>.<br><span class="badge badge-warning font-weight-bold mt-2">+${rewardGold} Gold akan dikreditkan</span>`
          : `Confirm approval for <b>${playerName}</b> on <b>${questName}</b>.<br><span class="badge badge-warning font-weight-bold mt-2">+${rewardGold} Gold will be credited</span>`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#10b981",
        cancelButtonColor: "#6b7280",
        confirmButtonText: `<i class="mdi mdi-check mr-1"></i> ${isId ? "Ya, Setujui" : "Yes, Approve"}`,
        cancelButtonText: this.$t("common.cancel"),
        background: "#141724",
      });

      if (result.isConfirmed) {
        try {
          this.actionLoading = item.id;
          await updateRewardApproval(item.id, "approved", "");
          item.status = "approved";
          Swal.fire({
            icon: "success",
            title: isId ? "Berhasil Disetujui" : "Approved",
            text: isId ? `Klaim quest ${playerName} telah disetujui.` : `Quest submission for ${playerName} has been approved.`,
            timer: 1800,
            showConfirmButton: false,
            background: "#141724",
          });
        } catch (err) {
          console.error(err);
          Swal.fire({
            icon: "error",
            title: this.$t("common.error"),
            text: err.response?.data?.message || (isId ? "Gagal memproses persetujuan." : "Failed to process approval."),
            background: "#141724",
          });
        } finally {
          this.actionLoading = null;
        }
      }
    },
    async quickReject(item) {
      const isId = this.$currentLocale === "id";
      const playerName = item.user?.name || "player";

      const { value: reason } = await Swal.fire({
        title: isId ? "Tolak Bukti Quest?" : "Reject Quest Submission?",
        html: isId ? `Masukkan alasan penolakan untuk <b>${playerName}</b>:` : `Enter rejection reason for <b>${playerName}</b>:`,
        input: "textarea",
        inputPlaceholder: isId ? "Contoh: Bukti screenshot tidak sesuai kriteria SOP atau tidak jelas..." : "e.g., Screenshot proof does not match SOP criteria or is unclear...",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        confirmButtonText: `<i class="mdi mdi-close mr-1"></i> ${isId ? "Tolak Bukti" : "Reject Submission"}`,
        cancelButtonText: this.$t("common.cancel"),
        background: "#141724",
        inputValidator: (value) => {
          if (!value) {
            return isId ? "Alasan penolakan wajib diisi agar player dapat memperbaiki!" : "Rejection reason is required!";
          }
        },
      });

      if (reason) {
        try {
          this.actionLoading = item.id;
          await updateRewardApproval(item.id, "rejected", reason);
          item.status = "rejected";
          item.rejection_notes = reason;
          Swal.fire({
            icon: "success",
            title: isId ? "Bukti Ditolak" : "Submission Rejected",
            text: isId ? "Catatan penolakan telah diteruskan ke player." : "Rejection notes have been sent to the player.",
            timer: 1800,
            showConfirmButton: false,
            background: "#141724",
          });
        } catch (err) {
          console.error(err);
          Swal.fire({
            icon: "error",
            title: this.$t("common.error"),
            text: err.response?.data?.message || (isId ? "Gagal memproses penolakan." : "Failed to process rejection."),
            background: "#141724",
          });
        } finally {
          this.actionLoading = null;
        }
      }
    },
    async batchApprove() {
      const isId = this.$currentLocale === "id";
      const pendingItems = this.rewardApproval.filter((i) => (i.status || "").toLowerCase() === "pending");
      if (pendingItems.length === 0) return;

      const result = await Swal.fire({
        title: isId ? `Setujui ${pendingItems.length} Klaim Quest?` : `Approve ${pendingItems.length} Quest Claims?`,
        text: isId
          ? `Semua ${pendingItems.length} pengajuan bukti yang menunggu akan diverifikasi dan poin gold akan langsung dikreditkan ke masing-masing player.`
          : `All ${pendingItems.length} pending submissions will be verified and Gold rewards will be credited immediately.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#10b981",
        cancelButtonColor: "#6b7280",
        confirmButtonText: isId ? `Ya, Setujui Semua (${pendingItems.length})` : `Yes, Approve All (${pendingItems.length})`,
        cancelButtonText: this.$t("common.cancel"),
        background: "#141724",
      });

      if (result.isConfirmed) {
        this.loading = true;
        try {
          for (const item of pendingItems) {
            await updateRewardApproval(item.id, "approved", "");
            item.status = "approved";
          }
          Swal.fire({
            icon: "success",
            title: isId ? "Batch Berhasil" : "Batch Approved",
            text: isId ? `${pendingItems.length} klaim quest telah disetujui sekaligus!` : `${pendingItems.length} quest claims approved!`,
            timer: 2000,
            showConfirmButton: false,
            background: "#141724",
          });
        } catch (err) {
          console.error(err);
          Swal.fire({
            icon: "error",
            title: this.$t("common.error"),
            text: isId ? "Terjadi kendala saat memproses beberapa klaim." : "An error occurred while processing some claims.",
            background: "#141724",
          });
        } finally {
          this.loading = false;
        }
      }
    },
  },
};
</script>
